// The single place environment configuration enters the app. Nothing else reads
// import.meta.env, so a missing or misspelled variable fails immediately and by
// name rather than surfacing later as an undefined somewhere unhelpful.
//
// **An outbound host needs two changes, not one**: the variable below *and* a
// matching entry in the CloudFront CSP in `application.yml`. Add only the first
// and the browser refuses the request before it is sent — a console violation
// and nothing in the UI.
//
// Values that do not differ per environment are constants here, not variables.

export type Environment = 'development' | 'polaris' | 'staging' | 'production';

const ENVIRONMENTS: readonly Environment[] = ['development', 'polaris', 'staging', 'production'];

const parseEnvironment = (value: string | undefined): Environment => {
  const found = ENVIRONMENTS.find((candidate) => candidate === value);
  if (found === undefined) {
    throw new Error(
      `VITE_APP_ENVIRONMENT is "${value ?? 'undefined'}", expected one of: ` +
        `${ENVIRONMENTS.join(', ')}. Locally, copy .env.example to .env.local.`,
    );
  }
  return found;
};

const required = (name: string, value: string | undefined): string => {
  if (value === undefined || value === '') {
    throw new Error(
      `${name} is missing. Every environment needs it — see .env.example. ` +
        `Locally, copy .env.example to .env.local.`,
    );
  }
  return value;
};

// Route paths the auth flow depends on. Exported so the router, the guard and
// the URIs handed to Cognito cannot drift apart — a redirect URI that does not
// match a registered callback fails with `redirect_mismatch`.
export const AUTH_CALLBACK_PATH = '/auth/callback';
export const LOGIN_PATH = '/login';

// The catalog's own routes, exported for the same reason as the two above, so a
// route rename is one edit.
//
// **The shape mirrors a record's `path`** — a method's is
// `experience-design/user-research/field-study` — so a link is
// `${SDLC_PATH}/${record.path}` with no reshaping. The stage is not a segment:
// it groups phases, it is not a level anything navigates to.
export const SDLC_PATH = '/sdlc';
export const SEARCH_PATH = '/search';
export const TOOLKIT_PATH = '/toolkit';
export const LUNA_TELEMETRY_PATH = '/toolkit/luna-telemetry';

// The base of an agent's page, `${AGENTS_PATH}/${name}`, **inside the Toolkit
// section** so the Toolkit tab stays lit under it. The library itself is
// `TOOLKIT_PATH`; this path alone redirects there.
export const AGENTS_PATH = '/toolkit/agents';

/**
 * Search's query-string keys.
 *
 * **The whole of search's state lives in the URL**, not in component state: a
 * search is pasted, reloaded and reached back to with the browser's own back
 * button, and component state survives none of those. It also means the AppBar's
 * field and the results page share one source of truth.
 *
 * Constants because the field writes them and the page reads them, and a typo on
 * one side is a search that silently finds nothing.
 */
export const SEARCH_QUERY_PARAM = 'q';
export const SEARCH_STAGE_PARAM = 'stage';
export const SEARCH_TYPE_PARAM = 'type';

// Where the content came from, for the "View in Confluence" link. **A constant
// rather than a variable**: every environment reads the same space, so one that
// could point somewhere else is a way for one of them to point nowhere.
//
// This is the one outbound host needing no CSP entry — a link is a navigation,
// not a request, and `connect-src` governs only the latter.
export const CONFLUENCE_BASE_URL = 'https://anddigitaltransformation.atlassian.net/wiki';

// Where the browser comes back to. **Derived from the running origin rather than
// configured**, because Vite's port is a preference and not a promise:
// `strictPort` is off, so it walks up from 3000 and two worktrees can serve at
// once. A configured redirect URI would point at whichever started first, and
// the symptom is Cognito's `redirect_mismatch` rather than anything local.
//
// The polaris app client registers localhost:3000 through :3004 for that reason.
// Past :3004, login fails until the port is pinned:
// `npm run dev -- --port 3000`.
const origin = window.location.origin;

export const config = {
  appName: 'Augment Toolkit',
  environment: parseEnvironment(import.meta.env.VITE_APP_ENVIRONMENT),

  // The shared HTTP API, origin only and no trailing slash — callers supply a
  // leading-slash path. Configured rather than derived: the API is a sibling
  // subdomain in non-prod but sits under a different parent domain in
  // production, so no rule produces both from window.location.
  //
  // Needs a `connect-src` entry too — see the note at the top of this file.
  apiBaseUrl: required('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL),

  auth: {
    // The **user pool** endpoint, not the hosted UI domain. Discovery from here
    // points at the hosted UI for the endpoints that live there.
    authority: required('VITE_AUTH_AUTHORITY', import.meta.env.VITE_AUTH_AUTHORITY),
    clientId: required('VITE_AUTH_CLIENT_ID', import.meta.env.VITE_AUTH_CLIENT_ID),

    // The hosted UI host, needed only to build the logout URL by hand: Cognito's
    // logout takes `client_id` and `logout_uri` rather than the standard
    // `id_token_hint` and `post_logout_redirect_uri`, so it cannot be discovered
    // even though the discovery document advertises an end_session_endpoint.
    hostedUiDomain: required(
      'VITE_AUTH_HOSTED_UI_DOMAIN',
      import.meta.env.VITE_AUTH_HOSTED_UI_DOMAIN,
    ),

    redirectUri: `${origin}${AUTH_CALLBACK_PATH}`,
    postLogoutRedirectUri: `${origin}${LOGIN_PATH}`,
  },

  analytics: {
    // Empty means analytics is off, which is the normal state everywhere except
    // production — hence optional rather than required().
    //
    // Needs script-src, connect-src and img-src entries — same note at the top
    // of this file, and three directives rather than one.
    measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID ?? '',
  },
} as const;

export const isProduction = (): boolean => config.environment === 'production';
